/**
 * Request-history response persistence, inside the CURRENT ACCOUNT'S data root
 * (Tauri only).
 *
 * Full responses are too large for localStorage (quota ~5MB), so each history
 * entry's response body/headers are written to a file on disk keyed by the
 * entry id:
 *
 *   <AppLocalData>/users/<accountId>/temp/history/responses/{entryId}.json
 *
 * localStorage keeps only the lightweight entry metadata + a stub marking the
 * response as on-disk.
 *
 * These files are whole HTTP exchanges — bodies AND request/response headers,
 * so bearer tokens, cookies and API keys. They used to sit in one device-global
 * folder that no sign-out ever cleared, readable by the next account to use the
 * app. Now the account root is part of every path built here, and sign-out
 * clears that root, vault aside (accountData.purgeAccountData).
 *
 * In non-Tauri (web/dev) builds the filesystem is unavailable, so callers fall
 * back to storing a (budgeted) inline response in localStorage instead.
 */
import { isTauri } from '../utils/tauri';
import { accountPath } from './accountData';
import { serializeResponse } from '../utils/workspaceTabPersistence';

const ROOT_REL = 'temp/history/responses';

let fsAvailableCache;

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

/** Absolute responses dir for the current account, or null if unavailable. */
async function rootDir() {
  return accountPath(ROOT_REL);
}

async function ensureDir() {
  const dir = await rootDir();
  if (!dir) throw new Error('no account data dir');
  const fs = await fsModule();
  await fs.mkdir(dir, { recursive: true });
  return dir;
}

/** True when responses can be persisted to the filesystem (Tauri + writable dir). */
export async function canPersistHistoryResponses() {
  if (fsAvailableCache !== undefined) return fsAvailableCache;
  if (!isTauri()) {
    fsAvailableCache = false;
    return false;
  }
  try {
    await ensureDir();
    fsAvailableCache = true;
  } catch (err) {
    console.warn('[historyResponseStore] AppLocalData storage unavailable:', err);
    fsAvailableCache = false;
  }
  return fsAvailableCache;
}

async function filePath(id) {
  const dir = await rootDir();
  return dir ? `${dir}/${String(id)}.json` : null;
}

/** Persist a full response for a history entry. Returns true on success. */
export async function writeHistoryResponse(id, response) {
  if (!id || !response || !(await canPersistHistoryResponses())) return false;
  try {
    const fs = await fsModule();
    const dir = await ensureDir();
    // serializeResponse keeps data/headers/byteMetrics; keep url too (it drops it).
    const payload = JSON.stringify({ ...serializeResponse(response), url: response.url ?? null });
    await fs.writeTextFile(`${dir}/${String(id)}.json`, payload);
    return true;
  } catch (err) {
    console.warn('[historyResponseStore] failed to write response', id, err);
    return false;
  }
}

/** Read a full response for a history entry, or null if missing/unavailable. */
export async function readHistoryResponse(id) {
  if (!id || !(await canPersistHistoryResponses())) return null;
  try {
    const path = await filePath(id);
    if (!path) return null;
    const fs = await fsModule();
    const raw = await fs.readTextFile(path);
    const parsed = JSON.parse(raw);
    return { ...serializeResponse(parsed), url: parsed.url ?? null };
  } catch {
    return null;
  }
}

/** Delete the response files for the given history entry ids (best-effort). */
export async function removeHistoryResponses(ids) {
  if (!ids?.length || !(await canPersistHistoryResponses())) return;
  const dir = await rootDir();
  if (!dir) return;
  const fs = await fsModule();
  await Promise.all(
    ids.map(async (id) => {
      try {
        await fs.remove(`${dir}/${String(id)}.json`);
      } catch {
        // ignore missing
      }
    })
  );
}
